/**
 * MiniPlayer — sticky bottom-of-screen mini-player that follows the user
 * across pages while a video plays. Inspired by YouTube/Spotify.
 *
 * Behaviour:
 *   • `<Watch />` calls `useMiniPlayer().setActive({ id, title, ... })`
 *     when it mounts, and `clear()` on full close.
 *   • Watch.jsx also calls `setPosition(seconds)` periodically while the
 *     video plays (throttled to once per ~2s). When the user clicks the
 *     mini-player thumb to return to /watch/:id, the URL gets
 *     `?t=<seconds>` appended so the player resumes from that exact
 *     second.
 *   • For simplicity we don't transfer the underlying <video> element;
 *     instead the mini-player renders an inline preview-image card with
 *     PLAY → returns the user to /watch/:id where the real player lives.
 *     This keeps the implementation simple and cross-route safe (HLS.js
 *     elements are notoriously fragile to portal moves).
 */
import React, { createContext, useContext, useState, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Play, X } from "lucide-react";

const Ctx = createContext(null);

export function MiniPlayerProvider({ children }) {
  const [active, setActiveRaw] = useState(null); // { id, title, thumbnail, creator, kind }
  const positionRef = useRef(0);                  // seconds — kept in a ref so
                                                  // setPosition does NOT cause a
                                                  // re-render every tick.
  const activeIdRef = useRef(null);
  activeIdRef.current = active?.id || null;

  // Stable callback refs so consuming components (e.g. Watch.jsx) don't
  // re-fire useEffects on every Provider render.
  const setActive = useCallback((next) => {
    if (!next || activeIdRef.current !== next.id) positionRef.current = 0;
    setActiveRaw(next);
  }, []);
  const setPosition = useCallback((sec) => { positionRef.current = Number(sec) || 0; }, []);
  const getPosition = useCallback(() => positionRef.current, []);
  const clear = useCallback(() => { positionRef.current = 0; setActiveRaw(null); }, []);

  return <Ctx.Provider value={{ active, setActive, setPosition, getPosition, clear }}>{children}</Ctx.Provider>;
}

export function useMiniPlayer() {
  return useContext(Ctx) || {
    active: null,
    setActive: () => {},
    setPosition: () => {},
    getPosition: () => 0,
    clear: () => {},
  };
}

export default function MiniPlayer() {
  const { active, clear, getPosition } = useMiniPlayer();
  const { pathname } = useLocation();

  if (!active) return null;
  const onWatch = pathname.startsWith("/watch/") || pathname.startsWith("/live/");
  if (onWatch) return null;

  // Resume URL: append ?t=<seconds> when there's a saved position so the
  // VOD player jumps straight to that timestamp on click. Live streams
  // skip the param (you can't seek in a live stream).
  const pos = Math.max(0, Math.floor(getPosition()));
  const watchUrl = active.kind === "live"
    ? `/live/${active.id}`
    : pos > 2 ? `/watch/${active.id}?t=${pos}` : `/watch/${active.id}`;

  return (
    <div
      data-testid="mini-player"
      className="fixed left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4 bottom-20 sm:bottom-4 z-[60] sm:w-[360px] bg-[#0a0a0a] border border-white/15 rounded-2xl shadow-2xl overflow-hidden flex items-center gap-2 p-2"
    >
      <Link to={watchUrl} data-testid="mini-player-thumb" className="relative w-20 h-12 rounded-lg overflow-hidden bg-black shrink-0">
        {active.thumbnail
          ? <img src={active.thumbnail} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-gradient-to-br from-red-500 to-rose-700" />}
        <span className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/20 transition">
          <Play className="w-5 h-5 text-white" />
        </span>
        {active.kind === "live" && (
          <span className="absolute top-1 left-1 bg-red-600 text-white text-[8px] font-black uppercase px-1 py-0.5 rounded animate-pulse">LIVE</span>
        )}
      </Link>
      <Link to={watchUrl} className="flex-1 min-w-0">
        <div data-testid="mini-player-title" className="text-white text-xs font-bold truncate">{active.title || "Now playing"}</div>
        {active.creator && <div className="text-white/50 text-[10px] truncate">{active.creator}</div>}
        {active.kind !== "live" && pos > 2 && (
          <div data-testid="mini-player-position" className="text-tv-accent text-[10px] font-bold">↻ Resume {Math.floor(pos / 60)}:{String(pos % 60).padStart(2, "0")}</div>
        )}
      </Link>
      <button
        data-testid="mini-player-close"
        onClick={clear}
        aria-label="Close mini player"
        className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center shrink-0"
      >
        <X className="w-4 h-4 text-white/70" />
      </button>
    </div>
  );
}
